<script setup lang="ts">
import { VisLine } from "@unovis/vue";
import { eventBus } from "#bb/lib/eventBus";
import { computed, provide } from "vue";
import { toChartVarName } from "@/lib/utils";
import { useChart } from "~/components/ui/chart";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
// useChart() is only available when inside ChartContainer (non-editor mode)
const chartConfig = computed(() => {
  try {
    const { config } = useChart();
    return config.value;
  } catch {
    return {};
  }
});

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Line editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this line chart",
        action: async () => {
          // @ts-ignore - dynamic event name not in mitt typed Events
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Line Chart",
    icon: "lucide:chart-line",
  },
});

// consume the same provided report data with lifecycle flags
type InjectedReport = { data: any[]; loading?: boolean; initialized?: boolean };
const report = inject<InjectedReport>("report", { data: [], loading: false, initialized: false });
const chartData = computed(() => (report.loading || !report.initialized ? [] : report.data));

// Compute the series keys either from settings or auto-detect
const seriesKeys = computed(() => {
  const configSeries = definitionData.value?.series;
  if (Array.isArray(configSeries) && configSeries.length > 0) {
    return configSeries;
  }
  const firstRow = chartData.value[0];
  if (firstRow && typeof firstRow === "object") {
    const keys = Object.keys(firstRow).filter((key) => key !== "name");
    const numericKeys = keys.filter((k) => typeof (firstRow as any)[k] === "number");
    return numericKeys.length ? numericKeys : keys;
  }
  return [];
});

// index-based x accessor; y accessors derived from the series keys
const x = (_d: any, i: number) => i;
const y = computed(() => {
  return seriesKeys.value.map((key) => {
    return (row: any) => row?.[key] ?? 0;
  });
});

const dataStep = computed(() => definitionData.value?.dataStep ?? 1);
const orientation = computed(() => definitionData.value?.orientation ?? "vertical");

const colorAccessorCode = computed(
  () => definitionData.value?.colorAccessor ?? ""
);

const isNoopAccessor = (code: string) => {
  const normalized = code.trim();
  if (!normalized)
    return true;
  if (/^\(?\s*[^)]*\s*\)?\s*=>\s*null\s*;?$/.test(normalized))
    return true;
  if (/return\s+null\s*;?$/.test(normalized))
    return true;
  return false;
};

const fallbackColor = computed(() => {
  const keys = seriesKeys.value;
  const cfg = chartConfig.value ?? {};
  return (_d: any, i: number) => {
    const key = keys[i];
    const varName = key ? toChartVarName(key) : "";
    if (!key || !cfg[key] || !varName)
      return undefined;
    return `var(--color-${varName})`;
  };
});

const color = computed(() => {
  if (isNoopAccessor(colorAccessorCode.value))
    return fallbackColor.value;
  try {
    const accessor = eval(colorAccessorCode.value);
    return typeof accessor === "function" ? accessor : fallbackColor.value;
  } catch (err) {
    console.error("Error evaluating color accessor:", err);
    return fallbackColor.value;
  }
});
</script>

<template>
  <div>
    <VisLine
      :data="chartData"
      :x="x"
      :y="y"
      :orientation="orientation"
      :dataStep="dataStep"
      :color="color"
    />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds"
export default {
  editor: () => import("#bb/components/editor/unovis/xy/line.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_XY_LINE,
  id: "xyline",
  icon: "lucide:chart-line",
  type: "Line Chart",
  description: "Line Chart is a Unovis component for displaying data in a line format.",
  default: {
    columnsSettings: { default: 1 },
    series: [],
    dataStep: 1,
    orientation: "vertical",
    colorAccessor: "(d) => { \n  const val = typeof d === 'object' ? d.score : d;\n  return val < 35 ? '#fb2c36' :\n         val < 50 ? '#fe9a00' :\n         '#00bc7d';\n}",
  },
  categories: ["UNOVIS"],
  order: 12004,
}
</script>
